import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Ban, ChevronLeft, ChevronRight, List, CalendarDays } from "lucide-react";
import { api, STATUS_LABELS } from "../lib/api";

const statusColor = {
  en_attente: "text-yellow-400 border-yellow-400/30",
  confirmee: "text-[#009246] border-[#009246]/40",
  refusee: "text-[#C8102E] border-[#C8102E]/40",
  annulee: "text-gray-400 border-gray-500/40",
};

export default function AdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [view, setView] = useState("liste");
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = useCallback(() => {
    const params = {};
    if (dateFilter) params.date = dateFilter;
    if (statusFilter) params.status = statusFilter;
    api.get("/admin/reservations", { params }).then((r) => setReservations(r.data)).catch(() => {});
    api.get("/admin/reservations").then((r) => setAllReservations(r.data)).catch(() => {});
  }, [dateFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/reservations/${id}`, { status });
      toast.success(`Réservation ${STATUS_LABELS[status].toLowerCase()}`);
      load();
    } catch { toast.error("Erreur lors de la mise à jour"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réservation ?")) return;
    try { await api.delete(`/admin/reservations/${id}`); toast.success("Supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  const isLunch = (t) => t && parseInt(t.split(":")[0], 10) < 17;
  const filtered = reservations.filter((r) => {
    if (serviceFilter === "midi") return isLunch(r.time);
    if (serviceFilter === "soir") return !isLunch(r.time);
    return true;
  });

  const calendarDays = useMemo(() => {
    const active = allReservations.filter((r) => !["annulee", "refusee"].includes(r.status));
    const byDate = {};
    active.forEach((r) => {
      if (!byDate[r.date]) byDate[r.date] = { midi: 0, soir: 0, couverts: 0 };
      byDate[r.date][isLunch(r.time) ? "midi" : "soir"] += 1;
      byDate[r.date].couverts += r.party_size || 0;
    });
    const first = new Date(month);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, date: ds, data: byDate[ds] });
    }
    return cells;
  }, [allReservations, month]);

  const todayStr = new Date().toISOString().split("T")[0];
  const monthLabel = month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif-elegant text-3xl font-bold">Réservations</h1>
        <div className="flex rounded-full border border-white/15 p-1">
          <button data-testid="res-view-liste" onClick={() => setView("liste")} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm ${view === "liste" ? "bg-[#D4AF37] text-black font-medium" : "text-gray-300"}`}>
            <List size={14} /> Liste
          </button>
          <button data-testid="res-view-calendrier" onClick={() => setView("calendrier")} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm ${view === "calendrier" ? "bg-[#D4AF37] text-black font-medium" : "text-gray-300"}`}>
            <CalendarDays size={14} /> Calendrier
          </button>
        </div>
      </div>

      {view === "calendrier" && (
        <div className="mb-10" data-testid="res-calendar">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="calendar-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 text-gray-400 hover:text-[#D4AF37]"><ChevronLeft size={20} /></button>
            <p className="font-serif-elegant text-xl font-semibold capitalize">{monthLabel}</p>
            <button data-testid="calendar-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 text-gray-400 hover:text-[#D4AF37]"><ChevronRight size={20} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-center text-xs text-gray-500 mb-2">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {calendarDays.map((cell, i) => cell === null ? <div key={`e${i}`} /> : (
              <button
                key={cell.date}
                data-testid={`calendar-day-${cell.date}`}
                onClick={() => { setDateFilter(cell.date); setView("liste"); }}
                className={`min-h-[76px] rounded-lg border p-1.5 text-left transition-colors duration-150 hover:border-[#D4AF37]/60 ${
                  cell.date === todayStr ? "border-[#D4AF37] bg-[#D4AF37]/10" : cell.data ? "border-white/15 bg-[#141414]" : "border-white/5 bg-transparent"
                }`}
              >
                <span className={`text-xs ${cell.date === todayStr ? "text-[#D4AF37] font-semibold" : "text-gray-400"}`}>{cell.day}</span>
                {cell.data && (
                  <div className="mt-1 space-y-0.5">
                    {cell.data.midi > 0 && <p className="text-[10px] rounded bg-[#D4AF37]/20 text-[#F3D568] px-1 py-0.5">☀ {cell.data.midi} midi</p>}
                    {cell.data.soir > 0 && <p className="text-[10px] rounded bg-white/10 text-gray-200 px-1 py-0.5">☾ {cell.data.soir} soir</p>}
                    <p className="text-[10px] text-gray-500">{cell.data.couverts} couv.</p>
                  </div>
                )}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Cliquez sur un jour pour voir le détail des réservations (annulées et refusées exclues).</p>
        </div>
      )}

      {view === "liste" && (<><div className="flex flex-wrap gap-3 mb-8">
        <input data-testid="res-filter-date" type="date" className="ic-input !w-auto" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
        <select data-testid="res-filter-status" className="ic-input !w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="confirmee">Confirmée</option>
          <option value="refusee">Refusée</option>
          <option value="annulee">Annulée</option>
        </select>
        <select data-testid="res-filter-service" className="ic-input !w-auto" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
          <option value="">Midi & Soir</option>
          <option value="midi">Déjeuner (midi)</option>
          <option value="soir">Dîner (soir)</option>
        </select>
        {(dateFilter || statusFilter || serviceFilter) && (
          <button onClick={() => { setDateFilter(""); setStatusFilter(""); setServiceFilter(""); }} className="text-sm text-gray-400 hover:text-white px-3">
            Réinitialiser
          </button>
        )}
      </div>
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-gray-500">Aucune réservation.</p>}
        {filtered.map((r) => (
          <div key={r.id} className="ic-card !transform-none p-5" data-testid={`admin-reservation-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-medium">{r.first_name} {r.last_name} <span className="text-gray-500 text-sm">· {r.party_size} pers.</span></p>
                <p className="text-sm text-gray-400 mt-1">{r.date} à {r.time} · {r.phone} · {r.email}</p>
                {r.special_request && <p className="text-xs text-gray-500 mt-1">Demande : {r.special_request}</p>}
                {r.weekly_menu_id && <p className="text-xs text-[#D4AF37] mt-1">Menu de la semaine sélectionné</p>}
                {r.event_id && <p className="text-xs text-[#D4AF37] mt-1">Réservation pour une soirée</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs border rounded-full px-3 py-1 ${statusColor[r.status]}`}>{STATUS_LABELS[r.status]}</span>
                {r.status !== "confirmee" && (
                  <button data-testid={`res-accept-${r.id}`} onClick={() => updateStatus(r.id, "confirmee")} className="p-2 rounded-lg bg-[#009246]/20 text-[#009246] hover:bg-[#009246]/30" title="Accepter"><Check size={16} /></button>
                )}
                {r.status !== "refusee" && (
                  <button data-testid={`res-refuse-${r.id}`} onClick={() => updateStatus(r.id, "refusee")} className="p-2 rounded-lg bg-[#C8102E]/20 text-[#ff6b81] hover:bg-[#C8102E]/30" title="Refuser"><X size={16} /></button>
                )}
                {r.status !== "annulee" && (
                  <button data-testid={`res-cancel-${r.id}`} onClick={() => updateStatus(r.id, "annulee")} className="p-2 rounded-lg bg-gray-700/40 text-gray-300 hover:bg-gray-700/60" title="Annuler"><Ban size={16} /></button>
                )}
                <button data-testid={`res-delete-${r.id}`} onClick={() => remove(r.id)} className="p-2 rounded-lg text-gray-500 hover:text-[#ff6b81]" title="Supprimer"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
}
